import { type ReactNode, useCallback, useContext } from 'react';
import { PermissionProvider } from './PermissionProvider';
import { PermissionContext, type PermissionResolver } from './permissionContext';

export interface ContextualBlockProps {
    /** The one permission the block applies to; every other key is answered by the outer resolver. */
    readonly permission: string;
    /** Why the control cannot be used right now, or `null` when nothing blocks it. */
    readonly reason: string | null;
    readonly children: ReactNode;
}

/**
 * Blocks one permission for a reason that can change — a blank field, a save in flight, a report
 * out of date — so the `PermissionGuard` below draws the control `aria-disabled` with that reason
 * (design-system §9 *Permission rendering*).
 *
 * It refines the nearest resolver for `permission` only: an `allowed` answer becomes
 * `denied-contextual` while `reason` is set. A denial stays as it was, so a control the role can
 * never use stays hidden and the block never makes it visible.
 */
export function ContextualBlock({ permission, reason, children }: ContextualBlockProps) {
    const outer = useContext(PermissionContext);
    const resolve = useCallback<PermissionResolver>(
        (asked, resource) => {
            const decision = outer(asked, resource);
            if (asked === permission && decision.kind === 'allowed' && reason !== null) {
                return { kind: 'denied-contextual', reason };
            }
            return decision;
        },
        [outer, permission, reason],
    );
    return <PermissionProvider resolve={resolve}>{children}</PermissionProvider>;
}
